'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useRxQuestionnaireQuestions,
  type RxClinicalQuestion,
} from '@/lib/api/rx';

/**
 * Read-only preview of a clinical-network questionnaire. The content
 * is authored on the care-network side — merchants can inspect
 * exactly what their patients will be asked at checkout.
 */
export function QuestionnairePreview({
  storeId,
  externalQuestionnaireId,
}: {
  storeId: string;
  externalQuestionnaireId: string | null | undefined;
}) {
  const { t } = useTranslation('rx');
  const query = useRxQuestionnaireQuestions(storeId, externalQuestionnaireId);

  if (!externalQuestionnaireId) return null;

  if (query.isLoading) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 rounded-lg border px-3 py-4 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('questionnairePreview.loading')}
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="text-destructive flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3 text-sm">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{t('questionnairePreview.error')}</span>
      </div>
    );
  }

  const questions = query.data?.questions ?? [];
  if (questions.length === 0) {
    return (
      <div className="text-muted-foreground rounded-lg border px-3 py-4 text-sm">
        {t('questionnairePreview.empty')}
      </div>
    );
  }

  return (
    <ol className="space-y-3">
      {questions.map((q, idx) => (
        <QuestionRow key={q.externalQuestionId} question={q} index={idx + 1} />
      ))}
    </ol>
  );
}

function QuestionRow({ question: q, index }: { question: RxClinicalQuestion; index: number }) {
  const { t } = useTranslation('rx');
  return (
    <li className="rounded-lg border px-3 py-2.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium leading-snug">
            <span className="text-muted-foreground mr-1.5 tabular-nums">{index}.</span>
            {q.title}
          </p>
          {q.description ? (
            <p className="text-muted-foreground mt-0.5 text-xs leading-relaxed">{q.description}</p>
          ) : null}
          {q.options.length > 0 ? (
            <ul className="text-muted-foreground mt-1.5 flex flex-wrap gap-1">
              {q.options.map((opt) => (
                <li
                  key={opt.value}
                  className="bg-muted rounded px-1.5 py-0.5 text-[11px] leading-tight"
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Badge variant="outline" className="text-[10px] uppercase">
            {q.type}
          </Badge>
          {q.required ? (
            <Badge variant="secondary" className="text-[10px] uppercase">
              {t('questionnairePreview.requiredBadge')}
            </Badge>
          ) : null}
        </div>
      </div>
    </li>
  );
}

/** Standalone dialog wrapper used from the products table. */
export function ViewQuestionnaireDialog({
  open,
  onOpenChange,
  storeId,
  externalQuestionnaireId,
  productName,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  storeId: string;
  externalQuestionnaireId: string | null | undefined;
  productName: string | null;
}) {
  const { t } = useTranslation('rx');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('questionnairePreview.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {productName
              ? t('questionnairePreview.dialogDescriptionWithName', { productName })
              : t('questionnairePreview.dialogDescriptionFallback')}
          </DialogDescription>
        </DialogHeader>
        <QuestionnairePreview
          storeId={storeId}
          externalQuestionnaireId={externalQuestionnaireId}
        />
      </DialogContent>
    </Dialog>
  );
}
